/**
 * Relay-side M365 connection hooks.
 *
 * The connection lifecycle:
 *   1. useStartM365Connect() — POST to relay-m365-auth-start, returns
 *      authorization_url. Caller redirects window to it.
 *   2. User redirects to Microsoft, consents, redirects back to
 *      /auth/callback/m365_full?code=...&state=...
 *   3. The callback page invokes useCompleteM365Callback() →
 *      relay-m365-auth-callback exchanges code for tokens.
 *   4. useM365Connection() reads the integration row to display status.
 *   5. useDisconnectM365() deletes the token + integration rows.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export type M365ConnectionStatus =
  | "connected"
  | "disconnected"
  | "reauth_required";

export interface M365ConnectionMeta {
  connected_email?: string | null;
  connected_display_name?: string | null;
  source?: string | null;
  // forward-compat — server may add fields without breaking the client
  [key: string]: unknown;
}

export interface M365Connection {
  status: M365ConnectionStatus;
  connected_at: string | null;
  last_sync_at: string | null;
  last_sync_status: string | null;
  metadata: M365ConnectionMeta | null;
  sync_role: string | null;
  last_polled_at: string | null;
  last_availability_sync_at: string | null;
}

const EMPTY_CONNECTION: M365Connection = {
  status: "disconnected",
  connected_at: null,
  last_sync_at: null,
  last_sync_status: null,
  metadata: null,
  sync_role: null,
  last_polled_at: null,
  last_availability_sync_at: null,
};

function toM365Connection(
  data:
    | {
        status?: string | null;
        connected_at?: string | null;
        last_sync_at?: string | null;
        last_sync_status?: string | null;
        metadata?: unknown;
        sync_role?: string | null;
        last_polled_at?: string | null;
        last_availability_sync_at?: string | null;
      }
    | null
    | undefined,
): M365Connection {
  if (!data) return EMPTY_CONNECTION;
  return {
    status: (data.status as M365ConnectionStatus) ?? "disconnected",
    connected_at: data.connected_at ?? null,
    last_sync_at: data.last_sync_at ?? null,
    last_sync_status: data.last_sync_status ?? null,
    metadata: (data.metadata as M365ConnectionMeta) ?? null,
    sync_role: data.sync_role ?? null,
    last_polled_at: data.last_polled_at ?? null,
    last_availability_sync_at: data.last_availability_sync_at ?? null,
  };
}

export interface UseM365ConnectionResult {
  userConnection: M365Connection;
  orgFallback: M365Connection;
  /** Effective connection — userConnection if present, else orgFallback. */
  data: M365Connection;
  isLoading: boolean;
}

/**
 * Loads both the user-scoped connection (if userId provided) and the
 * org-fallback connection (user_id IS NULL). The UI typically renders
 * both — user-scoped section + admin-only org-fallback section.
 *
 * Signature is backward-compatible with the single-arg form: the second
 * return value `.data` is the effective connection (user-scoped wins),
 * matching the old hook's `{ data, isLoading }` shape.
 */
export function useM365Connection(
  organizationId: string | null,
  userId?: string | null,
): UseM365ConnectionResult {
  const userQuery = useQuery({
    queryKey: ["m365-connection", organizationId, "user", userId ?? null],
    queryFn: async (): Promise<M365Connection> => {
      if (!organizationId || !userId) return EMPTY_CONNECTION;
      const { data, error } = await supabase
        .schema("public")
        .from("calendar_integrations")
        .select(
          "status, connected_at, last_sync_at, last_sync_status, metadata, sync_role, last_polled_at, last_availability_sync_at",
        )
        .eq("organization_id", organizationId)
        .eq("user_id", userId)
        .eq("provider", "m365_full")
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return toM365Connection(data);
    },
    enabled: !!organizationId && !!userId,
    staleTime: 30_000,
  });

  const orgQuery = useQuery({
    queryKey: ["m365-connection", organizationId, "org-fallback"],
    queryFn: async (): Promise<M365Connection> => {
      if (!organizationId) return EMPTY_CONNECTION;
      // calendar_integrations lives in public schema (Forge table); the
      // app's supabase client defaults to relay schema, so we hop schemas
      // explicitly here.
      const { data, error } = await supabase
        .schema("public")
        .from("calendar_integrations")
        .select(
          "status, connected_at, last_sync_at, last_sync_status, metadata, sync_role, last_polled_at, last_availability_sync_at",
        )
        .eq("organization_id", organizationId)
        .is("user_id", null)
        .eq("provider", "m365_full")
        .is("deleted_at", null)
        .maybeSingle();
      if (error) throw error;
      return toM365Connection(data);
    },
    enabled: !!organizationId,
    staleTime: 30_000,
  });

  const userConnection = userQuery.data ?? EMPTY_CONNECTION;
  const orgFallback = orgQuery.data ?? EMPTY_CONNECTION;
  const effective =
    userConnection.status === "connected" || userConnection.connected_at
      ? userConnection
      : orgFallback;

  return {
    userConnection,
    orgFallback,
    data: effective,
    isLoading: userQuery.isLoading || orgQuery.isLoading,
  };
}

export type M365ConnectScope = "user" | "org";

export function useStartM365Connect(
  organizationId: string | null,
  scope: M365ConnectScope = "user",
) {
  return useMutation({
    mutationFn: async (): Promise<{ authorization_url: string }> => {
      if (!organizationId) throw new Error("No active organization");
      const redirectUri = `${window.location.origin}/auth/callback/m365_full`;
      const { data, error } = await supabase.functions.invoke<{
        authorization_url: string;
      }>("relay-m365-auth-start", {
        body: {
          organization_id: organizationId,
          redirect_uri: redirectUri,
          scope,
        },
      });
      if (error) {
        // Replace the generic supabase-js wrapper ("Edge Function returned
        // a non-2xx status code") with the function's actual JSON error
        // body — same fix as useCompleteM365Callback below.
        const detail = await extractFunctionError(error);
        throw new Error(detail);
      }
      if (!data?.authorization_url) {
        throw new Error("relay-m365-auth-start returned no authorization URL");
      }
      return data;
    },
  });
}

interface CompleteCallbackResult {
  ok?: boolean;
  provider?: string;
  connected_email?: string | null;
  consent_required?: boolean;
  error?: string;
  detail?: string;
  microsoft_response?: string;
  microsoft_status?: number;
}

/**
 * Pull the JSON error body out of a supabase-js FunctionsHttpError so the
 * UI can show what the function actually returned (Microsoft AADSTS codes,
 * "Token encryption failed", etc.) rather than the generic
 * "Edge Function returned a non-2xx status code" wrapper.
 *
 * supabase-js v2 FunctionsHttpError shape: `{ message, context: Response }`
 * — the `context` property IS the Response object directly, not a wrapper
 * containing one. Different from v1 (which had `.context.response`).
 */
async function extractFunctionError(error: unknown): Promise<string> {
  const fallback = error instanceof Error ? error.message : "Unknown error";
  try {
    // .context is the Response object itself in supabase-js v2.
    const response = (error as { context?: Response | null }).context;
    if (!response || typeof response.text !== "function") return fallback;
    const text = await response.text();
    if (!text) return fallback;
    try {
      const body = JSON.parse(text) as CompleteCallbackResult;
      if (body.detail) return `${body.error ?? "Error"}: ${body.detail}`;
      if (body.microsoft_response) {
        return `${body.error ?? "Error"} (HTTP ${body.microsoft_status ?? "?"}): ${body.microsoft_response}`;
      }
      return body.error ?? text.slice(0, 500);
    } catch {
      return text.slice(0, 500);
    }
  } catch (extractErr) {
    // If the body has already been consumed (which supabase-js sometimes
    // does internally), we'll hit a TypeError. Surface a hint so we know.
    return `${fallback} (extract failed: ${extractErr instanceof Error ? extractErr.message : "unknown"})`;
  }
}

export function useCompleteM365Callback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { code: string; state: string }): Promise<CompleteCallbackResult> => {
      const redirectUri = `${window.location.origin}/auth/callback/m365_full`;
      const { data, error } = await supabase.functions.invoke<CompleteCallbackResult>(
        "relay-m365-auth-callback",
        { body: { code: input.code, state: input.state, redirect_uri: redirectUri } },
      );
      if (error) {
        // Replace the generic supabase-js wrapper with the function's
        // actual response body (Microsoft AADSTS code, encryption failure,
        // etc.) so the UI can show actionable diagnostic text.
        const detail = await extractFunctionError(error);
        throw new Error(detail);
      }
      return data ?? {};
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m365-connection"] });
    },
  });
}

/**
 * Disconnect (soft-delete) the M365 connection for the given scope.
 * Defaults to 'user' so accidental admin disconnects of the per-user
 * connection don't also wipe the org fallback. Pass scope='org' (and
 * userId=null) to disconnect the org-wide fallback row.
 */
export function useDisconnectM365(
  organizationId: string | null,
  options?: { scope?: M365ConnectScope; userId?: string | null },
) {
  const qc = useQueryClient();
  const scope: M365ConnectScope = options?.scope ?? "user";
  const userId = options?.userId ?? null;
  return useMutation({
    mutationFn: async () => {
      if (!organizationId) throw new Error("No active organization");
      if (scope === "user" && !userId) {
        throw new Error("userId required to disconnect user-scoped connection");
      }

      const nowIso = new Date().toISOString();

      // Soft-delete token row first (more sensitive). If integration
      // soft-delete then fails, the connection is still effectively dead
      // — no resolvable tokens, just a leftover label row. We log loudly.
      let tokUpdate = supabase
        .schema("public")
        .from("calendar_oauth_tokens")
        .update({ deleted_at: nowIso, updated_at: nowIso })
        .eq("organization_id", organizationId)
        .eq("provider", "m365_full")
        .is("deleted_at", null);
      tokUpdate = scope === "user"
        ? tokUpdate.eq("user_id", userId as string)
        : tokUpdate.is("user_id", null);
      const { error: tokErr } = await tokUpdate;
      if (tokErr) throw tokErr;

      let intUpdate = supabase
        .schema("public")
        .from("calendar_integrations")
        .update({ deleted_at: nowIso, updated_at: nowIso })
        .eq("organization_id", organizationId)
        .eq("provider", "m365_full")
        .is("deleted_at", null);
      intUpdate = scope === "user"
        ? intUpdate.eq("user_id", userId as string)
        : intUpdate.is("user_id", null);
      const { error: intErr } = await intUpdate;
      if (intErr) {
        console.warn(
          "[m365] integration row soft-delete failed (token row already detached):",
          intErr.message,
        );
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m365-connection", organizationId] });
    },
  });
}
